/**
 * Physical constants for croquet ball simulation.
 *
 * Sources:
 *   - Ball dimensions: Croquet Association specification
 *   - Hoop dimensions: CA competition standard (3¾" gap, 5/8" wire)
 *   - Grass friction: Rod Cross 2017, Don Gugan Oxford Croquet measurements
 *   - Restitution: estimated from steel-on-steel impact data
 */

/**
 * Physics uses a scaled unit system (1 unit ≈ 10cm real-world).
 * Real-world values multiplied by 10 so objects are visible on a 20×15 unit court.
 * Proportions are identical to real croquet — just inflated for display.
 */

// ── Ball ──────────────────────────────────────────────────────────────────────
/** Ball radius in display units (~0.46m real, scaled ×10) */
export const BALL_RADIUS = 0.46

/** Ball mass in kg */
export const BALL_MASS = 0.454

// ── Hoop ──────────────────────────────────────────────────────────────────────
/** Upright wire radius in display units (~0.16m real, scaled ×10) */
export const UPRIGHT_RADIUS = 0.16

/** Clear gap between uprights in display units (~0.95m real, scaled ×10) */
export const HOOP_GAP = 0.95

/** Total hoop width — gap + two wire radii (for geometry calcs) */
export const HOOP_WIDTH = HOOP_GAP + UPRIGHT_RADIUS * 2

// ── Friction ──────────────────────────────────────────────────────────────────
/**
 * Rolling deceleration in display units/s².
 * Real-world: 0.3–0.9 m/s². Scaled ×10 = 3–9 display units/s².
 * Start at 4.0 (medium-paced lawn). Tune in physics-notes.md.
 */
export const GRASS_FRICTION = 4.0

/** Ball stops when speed falls below this threshold (m/s) */
export const STOP_THRESHOLD = 0.02

// ── Restitution ───────────────────────────────────────────────────────────────
/** Coefficient of restitution for ball–upright collision (steel on steel) */
export const BALL_UPRIGHT_RESTITUTION = 0.5

/** Coefficient of restitution for ball–ball collision (for future multi-ball) */
export const BALL_BALL_RESTITUTION = 0.9

/** Coefficient of restitution for ball–boundary collision */
export const BOUNDARY_RESTITUTION = 0.3

// ── Shot power ────────────────────────────────────────────────────────────────
/**
 * Maximum initial ball speed when shot at full power (m/s).
 * A competitive croquet shot is ~6–8 m/s.
 * 7 yards (~6.4m) at medium power should reach with speed to spare.
 */
export const MAX_SHOT_SPEED = 20.0   // scaled ×10 from real-world ~7 m/s

/** Minimum shot speed even at zero power (so ball always moves) */
export const MIN_SHOT_SPEED = 0.5
